import { useEffect, useRef } from 'react';

import { useGetFieldGlassSubmissionStatusQuery } from '@libs/graphql-types';

interface UseSubmissionStatusPollingProps {
  matchId: string;
  enabled?: boolean;
  pollingInterval?: number; // in milliseconds, default 5 seconds
}

export const useSubmissionStatusPolling = ({
  matchId,
  enabled = true,
  pollingInterval = 3000,
}: UseSubmissionStatusPollingProps) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { data, loading, error, refetch } =
    useGetFieldGlassSubmissionStatusQuery({
      variables: { match_id: matchId },
      skip: !enabled || !matchId,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    });

  const submissionData = data?.fieldGlassCandidateIntroductionByJobMatchId;
  const isSubmitted =
    submissionData?.submission_status?.toUpperCase() === 'SUBMITTED';
  const isProcessing =
    submissionData?.submission_status?.toUpperCase() === 'PROCESSING';

  // Combine submitted and processing states - when true, user cannot manage the application
  const isNotManageable = isSubmitted || isProcessing;

  useEffect(() => {
    if (!enabled || !matchId || isSubmitted) {
      // Clear interval if polling is disabled or submission is complete
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Start polling
    intervalRef.current = setInterval(() => {
      refetch();
    }, pollingInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, matchId, isSubmitted, pollingInterval, refetch]);

  // Clear interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    data: submissionData,
    loading,
    error,
    isSubmitted,
    isProcessing,
    isNotManageable,
    refetch,
  };
};
